import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  HEARTBEATS_V2_DASHBOARD_ANALYTICS_FLAG,
  setHeartbeatsV2Enabled,
} from 'services/heartbeatsV2';

interface HeartbeatsV2FlagSyncProps {
  /** The uaa_id the mounted LaunchDarkly provider was initialized with. */
  ldUserKey: string | null;
  /** The uaa_id of the session as it is right now. */
  sessionUserKey?: string;
  children: JSX.Element;
}

/**
 * Mirrors the Heartbeats V2 flag into `services/heartbeatsV2` so that the
 * reporting services, which are plain functions rather than hooks, can read it
 * synchronously (DEV-5763).
 *
 * The write happens during render, not in a `useEffect`, and that is the whole
 * point of this component. Effects run child-first, so an effect here would run
 * *after* the effects of the analytics pages that issue the requests -- the
 * first request of a session would be sent with the default (V1) regardless of
 * the flag, which would look exactly like the flag not working. Rendering a
 * parent, by contrast, always precedes rendering and mounting its children.
 *
 * The write is idempotent, so re-rendering (including React's double-render in
 * StrictMode) cannot change the result.
 *
 * It also refuses to act on another identity's flags. `LaunchDarklyProvider`
 * keeps the previous provider mounted while it re-initializes, so after
 * `handleImpersonate` swaps the session there is a window where the app has
 * already changed user but `useFlags` still answers for the old one -- long
 * enough for a request to be issued. For a UI flag that is a cosmetic lag; for
 * a per-user rollout switch it would route a user the flag does not name to V2,
 * which is the one thing this switch exists to prevent. So while the provider's
 * identity and the session's disagree, and while the provider has no identity
 * at all because initialization failed, the answer is V1. It resolves itself as
 * soon as the new provider mounts.
 *
 * Only the user is compared, because that is the unit this flag is targeted by,
 * the same unit the backend allowlists use. The provider's LaunchDarkly context
 * also carries `business-account`, and it does *not* re-initialize when only the
 * account changes -- irrelevant while targeting is per user, but load-bearing
 * the day anyone adds a business-account rule to this flag.
 */
const HeartbeatsV2FlagSync: React.FC<HeartbeatsV2FlagSyncProps> = ({
  ldUserKey,
  sessionUserKey,
  children,
}) => {
  const flags = useFlags();

  const identityIsCurrent = Boolean(ldUserKey) && ldUserKey === sessionUserKey;

  setHeartbeatsV2Enabled(
    identityIsCurrent ? flags?.[HEARTBEATS_V2_DASHBOARD_ANALYTICS_FLAG] : false
  );

  return children;
};

export default HeartbeatsV2FlagSync;
